/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import {
  Users, Coins, Shield, BarChart3, Search, AlertTriangle, Ban, RotateCcw,
  Plus, Minus, Crown, Trash2, Activity, Star, TrendingUp, Globe2, MessageCircle, Flag
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import { toast } from 'sonner';

function Stat({ label, value, icon: Icon, color = 'text-fuchsia-400' }) {
  return (
    <Card className="bg-[#161622] border-white/10">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <span className="text-xs text-white/50 uppercase tracking-wider">{label}</span>
          {Icon && <Icon size={14} className={color} />}
        </div>
        <div className="mt-1 text-2xl font-display font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function OverviewTab({ stats, topSpenders, revenue }) {
  if (!stats) return <div className="text-white/50 text-sm">Loading…</div>;
  const max = Math.max(1, ...revenue.map(r => r.revenue_inr));
  return (
    <div className="space-y-4" data-testid="admin-tab-overview-content">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Total users" value={stats.users.total.toLocaleString()} icon={Users} color="text-cyan-400" />
        <Stat label="DAU"          value={stats.users.dau.toLocaleString()}   icon={Activity} color="text-emerald-400" />
        <Stat label="MAU"          value={stats.users.mau.toLocaleString()}   icon={TrendingUp} color="text-fuchsia-400" />
        <Stat label="Suspended"    value={stats.users.suspended.toLocaleString()} icon={Ban} color="text-rose-400" />
        <Stat label="Stars in circulation" value={stats.economy.total_stars_circulating.toLocaleString()} icon={Star} color="text-amber-300" />
        <Stat label="Revenue all-time"     value={`₹${stats.economy.revenue_inr_all_time.toLocaleString()}`} icon={Coins} color="text-emerald-300" />
        <Stat label="Revenue 30d"          value={`₹${stats.economy.revenue_inr_last_30d.toLocaleString()}`} icon={BarChart3} color="text-cyan-300" />
        <Stat label="Paid orders"          value={stats.economy.paid_orders.toLocaleString()} icon={Shield} color="text-fuchsia-300" />
        <Stat label="Gifts today"  value={stats.engagement.gifts_today.toLocaleString()} icon={Star} color="text-amber-300" />
        <Stat label="Planets"      value={stats.engagement.total_planets.toLocaleString()} icon={Globe2} color="text-cyan-300" />
        <Stat label="Total gifts"  value={stats.engagement.total_gifts.toLocaleString()} icon={Star} color="text-emerald-300" />
        <Stat label="Total messages" value={stats.engagement.total_messages.toLocaleString()} icon={MessageCircle} color="text-fuchsia-300" />
      </div>

      <Card className="bg-[#161622] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={14} className="text-cyan-400" />
            <span className="text-sm font-display">Revenue — last {revenue.length || 0} days</span>
          </div>
          {revenue.length === 0 ? (
            <div className="text-xs text-white/40">No revenue yet.</div>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {revenue.map(r => (
                <div key={r.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${r.date} — ₹${r.revenue_inr.toLocaleString()}`}>
                  <div className="w-full rounded-t bg-gradient-to-t from-fuchsia-500 to-cyan-400 hover:opacity-90 transition"
                       style={{ height: `${(r.revenue_inr / max) * 100}%`, minHeight: 2 }} />
                  <span className="text-[9px] text-white/40 truncate">{r.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-[#161622] border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={14} className="text-amber-300" />
            <span className="text-sm font-display">Top spenders</span>
          </div>
          {topSpenders.length === 0 ? (
            <div className="text-xs text-white/40">No paid orders yet.</div>
          ) : (
            <div className="space-y-2">
              {topSpenders.map((s, i) => (
                <div key={s.user_id} className="flex items-center gap-3 p-2 rounded-lg bg-white/5">
                  <span className="w-6 text-center text-xs text-white/40">#{i + 1}</span>
                  {s.avatar && <img src={s.avatar} alt="" className="w-7 h-7 rounded-full" />}
                  <span className="flex-1 truncate text-sm">{s.displayName}</span>
                  <span className="text-xs text-amber-300">{s.stars_bought.toLocaleString()} ⭐</span>
                  <span className="text-sm font-bold text-emerald-300">₹{s.spent_inr.toLocaleString()}</span>
                  <span className="text-[10px] text-white/40">{s.orders} order(s)</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function UsersTab() {
  const [q, setQ] = useState('');
  const [data, setData] = useState({ total: 0, users: [] });
  const [skip, setSkip] = useState(0);
  const [adjusting, setAdjusting] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}&skip=${skip}&limit=25`);
      setData(data);
    } catch (e) { toast.error('Could not load users'); }
  };

  useEffect(() => { load(); }, [skip]);

  /* eslint-disable react-hooks/set-state-in-effect */
  const confirmAndPost = async (path, message, body = {}) => {
    try {
      await api.post(path, body);
      toast.success(message);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  /* eslint-enable react-hooks/set-state-in-effect */

  const suspend = (u) => {
    const reason = window.prompt('Reason for suspension?');
    if (reason === null) return;
    confirmAndPost(`/admin/users/${u.user_id}/suspend`, `Suspended ${u.displayName}`, { reason });
  };
  const unsuspend = (u) => confirmAndPost(`/admin/users/${u.user_id}/unsuspend`, `Unsuspended ${u.displayName}`);
  const toggleAdmin = (u) => {
    const next = !u.is_admin;
    if (!window.confirm(`${next ? 'Promote' : 'Demote'} ${u.displayName}?`)) return;
    confirmAndPost(`/admin/users/${u.user_id}/role`, 'Role updated', { is_admin: next });
  };
  const adjust = async (delta, reason) => {
    try {
      const { data: r } = await api.post(`/admin/users/${adjusting.user_id}/adjust-stars`, { delta, reason });
      toast.success(`${r.delta > 0 ? '+' : ''}${r.delta.toLocaleString()} ⭐ — new balance ${r.balance.toLocaleString()}`);
      setAdjusting(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="admin-tab-users-content">
      <div className="flex gap-2">
        <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name, email, user_id…"
               className="bg-[#0F0F18] border-white/10" data-testid="admin-user-search-input"
               onKeyDown={e => e.key === 'Enter' && (setSkip(0), load())} />
        <Button onClick={() => { setSkip(0); load(); }} data-testid="admin-user-search-btn"
                className="bg-fuchsia-600 hover:bg-fuchsia-500">
          <Search size={14} className="mr-1" /> Search
        </Button>
      </div>
      <div className="text-xs text-white/40">{data.total} users · showing {data.users.length}</div>

      <div className="space-y-2">
        {data.users.map(u => (
          <div key={u.user_id} className="flex items-center gap-3 p-3 rounded-xl bg-[#161622] border border-white/10"
               data-testid={`admin-user-row-${u.user_id}`}>
            {u.avatar && <img src={u.avatar} alt="" className="w-9 h-9 rounded-full" />}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold truncate">{u.displayName}</span>
                {u.is_admin && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-400/20 text-amber-300 font-bold">ADMIN</span>}
                {u.suspended && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 font-bold">SUSPENDED</span>}
              </div>
              <div className="text-[11px] text-white/40 truncate">{u.email} · ⭐ {u.stars?.toLocaleString() || 0} · rank {u.rank}</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={() => setAdjusting(u)}
                      data-testid={`admin-adjust-${u.user_id}`}
                      className="text-amber-300 hover:bg-amber-500/10 h-8 px-2"
                      title="Adjust stars">
                <Star size={12} />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => toggleAdmin(u)}
                      className="text-fuchsia-300 hover:bg-fuchsia-500/10 h-8 px-2"
                      title={u.is_admin ? 'Demote' : 'Promote to admin'}>
                <Crown size={12} />
              </Button>
              {u.suspended ? (
                <Button size="sm" variant="ghost" onClick={() => unsuspend(u)}
                        className="text-emerald-300 hover:bg-emerald-500/10 h-8 px-2"
                        title="Unsuspend">
                  <RotateCcw size={12} />
                </Button>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => suspend(u)}
                        data-testid={`admin-suspend-${u.user_id}`}
                        className="text-rose-300 hover:bg-rose-500/10 h-8 px-2"
                        title="Suspend">
                  <Ban size={12} />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs">
        <Button size="sm" variant="ghost" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - 25))}
                className="text-white/60 hover:text-white">← Prev</Button>
        <span className="text-white/40">{skip + 1} – {Math.min(skip + data.users.length, data.total)} of {data.total}</span>
        <Button size="sm" variant="ghost" disabled={skip + data.users.length >= data.total} onClick={() => setSkip(skip + 25)}
                className="text-white/60 hover:text-white">Next →</Button>
      </div>

      <AdjustStarsDialog key={adjusting?.user_id || 'none'} user={adjusting} onCancel={() => setAdjusting(null)} onSubmit={adjust} />
    </div>
  );
}

function AdjustStarsDialog({ user, onCancel, onSubmit }) {
  const [delta, setDelta] = useState('1000');
  const [reason, setReason] = useState('');
  if (!user) return null;
  return (
    <Dialog open={!!user} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-sm" data-testid="admin-adjust-dialog">
        <DialogHeader><DialogTitle>Adjust ⭐ for {user.displayName}</DialogTitle></DialogHeader>
        <div className="text-xs text-white/50">Current: <b>{user.stars?.toLocaleString()}</b></div>
        <Input type="number" value={delta} onChange={e => setDelta(e.target.value)} placeholder="Delta (positive or negative)"
               className="bg-[#0F0F18] border-white/10" data-testid="admin-adjust-delta-input" />
        <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (optional)"
               className="bg-[#0F0F18] border-white/10" />
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onCancel} className="text-white/70">Cancel</Button>
          <Button onClick={() => onSubmit(-Math.abs(parseInt(delta) || 0), reason)} variant="ghost"
                  className="bg-rose-500/20 text-rose-200 hover:bg-rose-500/30">
            <Minus size={12} className="mr-1" /> Debit
          </Button>
          <Button onClick={() => onSubmit(Math.abs(parseInt(delta) || 0), reason)}
                  data-testid="admin-adjust-credit-btn"
                  className="bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0">
            <Plus size={12} className="mr-1" /> Credit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ModerationTab() {
  const [msgs, setMsgs] = useState([]);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let alive = true;
    api.get('/admin/messages?limit=50')
      .then(r => { if (alive) setMsgs(r.data); })
      .catch(() => toast.error('Could not load messages'));
    return () => { alive = false; };
  }, [tick]);

  const del = (m) => {
    if (!window.confirm(`Delete this message?\n\n"${m.text}"`)) return;
    api.delete(`/admin/messages/${m.id}`)
      .then(() => { toast.success('Deleted'); setTick(t => t + 1); })
      .catch(e => toast.error(e.response?.data?.detail || 'Failed'));
  };

  return (
    <div className="space-y-2" data-testid="admin-tab-moderation-content">
      <div className="text-xs text-white/40 flex items-center gap-2">
        <AlertTriangle size={12} className="text-amber-400" /> {msgs.length} recent messages across all planets
      </div>
      {msgs.map(m => (
        <div key={m.id} className="p-3 rounded-xl bg-[#161622] border border-white/10 flex items-start gap-3"
             data-testid={`admin-msg-${m.id}`}>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] text-white/40">
              {m.sender_name || m.sender_id} · planet {m.planet_id} · {m.type}
            </div>
            <div className="text-sm text-white/90 break-words mt-1">{m.text || <em className="text-white/40">(no text)</em>}</div>
          </div>
          <Button size="sm" variant="ghost" onClick={() => del(m)}
                  className="text-rose-300 hover:bg-rose-500/10 h-8 px-2" title="Delete message">
            <Trash2 size={12} />
          </Button>
        </div>
      ))}
      {msgs.length === 0 && <div className="text-xs text-white/40">No messages yet.</div>}
    </div>
  );
}

function ReportsTab() {
  const [data, setData] = useState({ pending_count: 0, reports: [] });
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [notes, setNotes] = useState({}); // per-report notes

  const reload = async (status = filter) => {
    setLoading(true);
    try {
      const params = status ? `?status=${status}&limit=100` : '?limit=100';
      const r = await api.get(`/admin/reports${params}`);
      setData(r.data);
    } catch {
      toast.error('Could not load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(filter); /* eslint-disable-next-line */ }, [filter]);

  const resolve = async (rpt, action) => {
    setBusyId(rpt.id);
    try {
      await api.post(`/admin/reports/${rpt.id}/resolve`, {
        action,
        notes: (notes[rpt.id] || '').trim() || undefined,
      });
      toast.success(action === 'dismiss' ? 'Report dismissed' : action === 'suspend' ? 'User suspended' : 'Warning logged');
      await reload(filter);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not resolve');
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-3" data-testid="admin-tab-reports-content">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-white/40">Filter:</span>
          {['pending', 'actioned', 'dismissed', ''].map(f => (
            <button
              key={f || 'all'}
              onClick={() => setFilter(f)}
              data-testid={`reports-filter-${f || 'all'}`}
              className={`px-3 py-1 rounded-full font-semibold transition ${
                filter === f
                  ? 'bg-rose-500/20 text-rose-200 border border-rose-400/40'
                  : 'bg-white/3 text-white/55 border border-white/8 hover:bg-white/8'
              }`}
            >
              {f || 'all'}
            </button>
          ))}
        </div>
        <div className="text-[11px] text-white/40">
          <span className="text-rose-300 font-bold">{data.pending_count}</span> pending · {data.reports.length} shown
        </div>
      </div>

      {loading && <div className="text-xs text-white/40">Loading…</div>}
      {!loading && data.reports.length === 0 && (
        <div className="text-xs text-white/40 p-6 rounded-2xl bg-white/3 border border-white/8 text-center">
          No {filter || ''} reports right now. The cosmos is calm. ✨
        </div>
      )}

      {data.reports.map(r => (
        <div key={r.id} className="rounded-2xl p-4 bg-[#161622] border border-white/10 space-y-3" data-testid={`admin-report-${r.id}`}>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {r.target.avatar && <img src={r.target.avatar} alt="" className="w-9 h-9 rounded-full ring-2 ring-rose-400/40" />}
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold truncate">{r.target.displayName}</span>
                  {r.target.suspended && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-rose-500/20 text-rose-300 uppercase tracking-wider">Suspended</span>}
                </div>
                <div className="text-[11px] text-white/45">
                  reported by <span className="text-white/70">{r.reporter.displayName}</span>
                  {' · '}{new Date(r.created_at).toLocaleString()}
                </div>
              </div>
            </div>
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
              r.status === 'pending' ? 'bg-amber-400/20 text-amber-200'
                : r.status === 'actioned' ? 'bg-emerald-500/20 text-emerald-300'
                : 'bg-white/10 text-white/60'
            }`}>{r.status}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Flag size={12} className="text-rose-300 shrink-0" />
            <span className="font-semibold text-rose-200">{r.reason_label || r.reason}</span>
          </div>
          {r.details && (
            <div className="text-xs text-white/70 leading-relaxed bg-white/3 rounded-xl px-3 py-2 whitespace-pre-wrap">
              {r.details}
            </div>
          )}
          {(r.planet_id || r.message_id) && (
            <div className="text-[10px] text-white/35">
              {r.planet_id && <span>planet: <span className="text-white/55">{r.planet_id}</span> </span>}
              {r.message_id && <span>· msg: <span className="text-white/55">{r.message_id}</span></span>}
            </div>
          )}

          {r.status === 'pending' ? (
            <>
              <Input
                value={notes[r.id] || ''}
                onChange={(e) => setNotes(n => ({ ...n, [r.id]: e.target.value }))}
                placeholder="Resolution notes (optional)"
                className="bg-white/5 border-white/10 text-white text-sm"
                data-testid={`report-notes-${r.id}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busyId === r.id}
                  onClick={() => resolve(r, 'dismiss')}
                  data-testid={`report-dismiss-${r.id}`}
                  className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
                >
                  Dismiss
                </Button>
                <Button
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() => resolve(r, 'warn')}
                  data-testid={`report-warn-${r.id}`}
                  className="bg-amber-500/90 hover:bg-amber-500 text-black font-semibold"
                >
                  Warn
                </Button>
                <Button
                  size="sm"
                  disabled={busyId === r.id}
                  onClick={() => resolve(r, 'suspend')}
                  data-testid={`report-suspend-${r.id}`}
                  className="bg-rose-500 hover:bg-rose-600 text-white"
                >
                  <Ban size={12} className="mr-1" /> Suspend user
                </Button>
              </div>
            </>
          ) : (
            <div className="text-[11px] text-white/45">
              Resolved · action: <span className="text-white/70">{r.resolution_action}</span>
              {r.resolution_notes ? <> · notes: <span className="text-white/70">{r.resolution_notes}</span></> : null}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function PaymentsTab() {
  const [payments, setPayments] = useState([]);
  useEffect(() => {
    api.get('/admin/payments?limit=50').then(r => setPayments(r.data)).catch(() => toast.error('Could not load'));
  }, []);
  return (
    <div className="space-y-2" data-testid="admin-tab-payments-content">
      <div className="text-xs text-white/40">{payments.length} most recent orders</div>
      {payments.map(p => (
        <div key={p.order_id} className="p-3 rounded-xl bg-[#161622] border border-white/10 flex items-center gap-3"
             data-testid={`admin-payment-${p.order_id}`}>
          {p.buyer?.avatar && <img src={p.buyer.avatar} alt="" className="w-7 h-7 rounded-full" />}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">{p.buyer?.displayName || p.user_id}</div>
            <div className="text-[11px] text-white/40 truncate">{p.pack_name} · {p.total_stars?.toLocaleString()} ⭐ · {p.order_id}</div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${
            p.status === 'paid' ? 'bg-emerald-500/20 text-emerald-300'
              : p.status === 'failed' ? 'bg-rose-500/20 text-rose-300'
              : 'bg-amber-400/20 text-amber-200'
          }`}>{p.status}</span>
          <span className="text-sm font-bold text-emerald-300">₹{p.amount_inr?.toLocaleString()}</span>
        </div>
      ))}
      {payments.length === 0 && <div className="text-xs text-white/40">No orders yet.</div>}
    </div>
  );
}

export default function Admin() {
  const { user, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [topSpenders, setTopSpenders] = useState([]);
  const [revenue, setRevenue] = useState([]);

  useEffect(() => {
    if (!user?.is_admin) return;
    Promise.all([
      api.get('/admin/stats').then(r => setStats(r.data)),
      api.get('/admin/top-spenders?limit=10').then(r => setTopSpenders(r.data)),
      api.get('/admin/revenue-chart?days=30').then(r => setRevenue(r.data)),
    ]).catch(() => toast.error('Failed to load admin data'));
  }, [user?.is_admin]);

  // Wait for auth check before deciding what to render
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]" data-testid="admin-loading">
        <div className="flex flex-col items-center gap-3 text-white/60">
          <Shield size={28} className="text-fuchsia-400 animate-pulse" />
          <span className="text-sm">Loading admin console…</span>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_admin) {
    return (
      <div className="space-y-4" data-testid="admin-forbidden">
        <div className="rounded-3xl p-8 bg-[#161622] border border-rose-500/30 text-center">
          <Shield size={32} className="mx-auto text-rose-400" />
          <h1 className="font-display text-2xl font-bold mt-3">Admin access only</h1>
          <p className="text-sm text-white/60 mt-2">
            Your account ({user.email}) is not an administrator. If this is a mistake, ask another admin to promote you.
          </p>
          <Button asChild className="mt-5 bg-gradient-to-r from-fuchsia-500 to-cyan-500 text-white border-0 rounded-full">
            <a href="/app">Back to WorldVibe</a>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-dashboard">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-cyan-500 flex items-center justify-center">
          <Shield size={18} />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold">Admin console</h1>
          <p className="text-xs text-white/50">Manage users, content, economy and observe analytics in one place.</p>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#0F0F18] border border-white/10">
          <TabsTrigger value="overview" className="data-[state=active]:bg-white/10" data-testid="admin-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-white/10" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="moderation" className="data-[state=active]:bg-white/10" data-testid="admin-tab-moderation">Moderation</TabsTrigger>
          <TabsTrigger value="reports" className="data-[state=active]:bg-white/10" data-testid="admin-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-white/10" data-testid="admin-tab-payments">Payments</TabsTrigger>
        </TabsList>
        <TabsContent value="overview"><OverviewTab stats={stats} topSpenders={topSpenders} revenue={revenue} /></TabsContent>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="moderation"><ModerationTab /></TabsContent>
        <TabsContent value="reports"><ReportsTab /></TabsContent>
        <TabsContent value="payments"><PaymentsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
